import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Eye, MessageCircle, ShoppingBag, Copy, ExternalLink, Trash2, Car, Wrench, TrendingUp } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import { confirmDialog } from '../lib/confirm';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const STATUSSEN = ['Concept', 'Gepubliceerd', 'Gereserveerd', 'Verkocht', 'Verborgen'];
const STATUS_KLEUR = { Concept: '#64748b', Gepubliceerd: '#16a34a', Gereserveerd: '#f59e0b', Verkocht: '#7c3aed', Verborgen: '#94a3b8' };

export default function Showroom() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slug, setSlug] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([carsysApi.getShowroomMine(), carsysApi.getShowroomStats().catch(() => null)]);
      setItems(m); setStats(s);
      if (m[0]?.slug) setSlug(m[0].slug);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const publicUrl = slug ? `${window.location.origin}/showroom/${slug}` : '';
  const setStatus = async (it, status) => {
    try { await carsysApi.setShowroomStatus(it.id, status); toast.success(t('Status bijgewerkt', 'Status bijgewerkt')); load(); }
    catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
  };
  const verwijder = async (it) => { if (!(await confirmDialog(`${t('Verwijderen uit showroom?', 'Verwijderen uit showroom?')}\n${it.titel || it.naam}`))) return; await carsysApi.deleteShowroom(it.id); toast.success(t('Verwijderd', 'Verwijderd')); load(); };

  const fotoUrl = (f) => (typeof f === 'string' ? f : (f?.dataUrl || f?.url || ''));
  const buildTekst = (it) => `${it.titel || it.naam || ''}\n\n${it.aiTekst || it.beschrijving || ''}\n\n${t('Prijs', 'Prijs')}: ${eur(it.prijs)}`.trim();
  const downloadFotos = (it) => {
    const fotos = (it.fotos || []).map(fotoUrl).filter(Boolean);
    if (!fotos.length) return 0;
    const basis = (it.titel || it.naam || 'advertentie').replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').toLowerCase() || 'advertentie';
    fotos.forEach((src, i) => { setTimeout(() => { const a = document.createElement('a'); a.href = src; a.download = `${basis}_${i + 1}.jpg`; document.body.appendChild(a); a.click(); a.remove(); }, i * 600); });
    return fotos.length;
  };
  const exporteer = (it, platform) => {
    navigator.clipboard?.writeText(buildTekst(it));
    const n = downloadFotos(it);
    const url = platform === 'facebook'
      ? (it.type === 'occasion' ? 'https://www.facebook.com/marketplace/create/vehicle' : 'https://www.facebook.com/marketplace/create/item')
      : 'https://www.marktplaats.nl/plaats';
    window.open(url, '_blank', 'noopener');
    toast.success(platform === 'facebook' ? t('Klaar voor Facebook Marketplace', 'Klaar voor Facebook Marketplace') : t('Klaar voor Marktplaats', 'Klaar voor Marktplaats'), {
      description: `${t('Tekst gekopieerd', 'Tekst gekopieerd')}${n ? ` · ${n} ${t('foto(\'s) gedownload', 'foto(\'s) gedownload')}` : ''}. ${t('Plak de tekst en voeg de foto\'s toe.', 'Plak de tekst en voeg de foto\'s toe.')}`,
    });
  };

  return (
    <div className="space-y-4" data-testid="showroom-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Globe size={22} color="#7c3aed" /><div><h2 style={{ margin: 0 }}>{t('Showroom', 'Showroom')}</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--cs-text-muted)' }}>{t('Je publieke verkooppagina', 'Je publieke verkooppagina')}</p></div>
        </div>
        {publicUrl && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cs-btn" onClick={() => { navigator.clipboard?.writeText(publicUrl); toast.success(t('Link gekopieerd', 'Link gekopieerd')); }} data-testid="showroom-copy-link"><Copy size={14} /> {t('Kopieer link', 'Kopieer link')}</button>
            <a className="cs-btn primary" href={`/showroom/${slug}`} target="_blank" rel="noreferrer" data-testid="showroom-open-public"><ExternalLink size={14} /> {t('Bekijk showroom', 'Bekijk showroom')}</a>
          </div>
        )}
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }} data-testid="showroom-stats">
          <Kpi icon={<Eye size={18} />} kleur="#2563eb" label={t('Bezoekers (views)', 'Bezoekers (views)')} val={stats.bezoekers} testid="sr-kpi-bezoekers" />
          <Kpi icon={<Globe size={18} />} kleur="#16a34a" label={t('Gepubliceerd', 'Gepubliceerd')} val={stats.gepubliceerd} testid="sr-kpi-gepubliceerd" />
          <Kpi icon={<MessageCircle size={18} />} kleur="#7c3aed" label={t('Contactaanvragen', 'Contactaanvragen')} val={stats.contactaanvragen} testid="sr-kpi-contact" />
          <Kpi icon={<ShoppingBag size={18} />} kleur="#ea580c" label={t('Verkocht', 'Verkocht')} val={stats.verkochteAdvertenties} testid="sr-kpi-verkocht" />
        </div>
      )}
      {stats && (stats.meestBekekenAuto?.titel || stats.meestBekekenOnderdeel?.titel) && (
        <div className="cs-card padded" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {stats.meestBekekenAuto?.titel && <div><div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 600 }}>{t('Meest bekeken auto', 'Meest bekeken auto')}</div><div style={{ fontWeight: 700 }}>{stats.meestBekekenAuto.titel} · {stats.meestBekekenAuto.views} {t('views', 'views')}</div></div>}
          {stats.meestBekekenOnderdeel?.titel && <div><div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 600 }}>{t('Meest bekeken onderdeel', 'Meest bekeken onderdeel')}</div><div style={{ fontWeight: 700 }}>{stats.meestBekekenOnderdeel.titel} · {stats.meestBekekenOnderdeel.views} {t('views', 'views')}</div></div>}
        </div>
      )}

      <div className="cs-card padded">
        {loading ? <div className="cs-empty">{t('Laden…', 'Laden…')}</div> : items.length === 0 ? (
          <div className="cs-empty" data-testid="showroom-empty">{t('Nog niets gepubliceerd. Maak een advertentie en klik op "Publiceer in showroom".', 'Nog niets gepubliceerd. Maak een advertentie en klik op "Publiceer in showroom".')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="cs-table" data-testid="showroom-table">
              <thead><tr><th></th><th>{t('Titel', 'Titel')}</th><th>{t('Type', 'Type')}</th><th>{t('Prijs', 'Prijs')}</th><th>{t('Views', 'Views')}</th><th>{t('Status', 'Status')}</th><th></th></tr></thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} data-testid={`sr-row-${it.id}`}>
                    <td>{it.type === 'occasion' ? <Car size={16} /> : <Wrench size={16} />}</td>
                    <td style={{ fontWeight: 600 }}>{it.titel || it.naam}</td>
                    <td>{it.type === 'occasion' ? t('Auto', 'Auto') : t('Onderdeel', 'Onderdeel')}</td>
                    <td>{eur(it.prijs)}</td>
                    <td>{it.views || 0}</td>
                    <td>
                      <select value={it.status} onChange={e => setStatus(it, e.target.value)} data-testid={`sr-status-${it.id}`}
                        className="cs-input" style={{ padding: '4px 6px', fontSize: 12.5, fontWeight: 600, color: STATUS_KLEUR[it.status], maxWidth: 150 }}>
                        {STATUSSEN.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button onClick={() => exporteer(it, 'marktplaats')} data-testid={`sr-marktplaats-${it.id}`} title={t('Klaarzetten voor Marktplaats', 'Klaarzetten voor Marktplaats')}
                          className="cs-btn ghost" style={{ color: '#ea580c', fontSize: 12, padding: '5px 8px' }}><ExternalLink size={13} /> Marktplaats</button>
                        <button onClick={() => exporteer(it, 'facebook')} data-testid={`sr-facebook-${it.id}`} title={t('Klaarzetten voor Facebook Marketplace', 'Klaarzetten voor Facebook Marketplace')}
                          className="cs-btn ghost" style={{ color: '#1877f2', fontSize: 12, padding: '5px 8px' }}><ExternalLink size={13} /> Facebook</button>
                        <button onClick={() => verwijder(it)} data-testid={`sr-del-${it.id}`} style={{ width: 32, height: 30, border: '1px solid var(--cs-border)', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const Kpi = ({ icon, kleur, label, val, testid }) => (
  <div data-testid={testid} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
    <div style={{ width: 38, height: 38, borderRadius: 9, background: `${kleur}1a`, color: kleur, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
    <div><div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 600 }}>{label}</div><div style={{ fontSize: 20, fontWeight: 800 }}>{val}</div></div>
  </div>
);
